import {
  AudioOutlined,
  FileImageOutlined,
  PaperClipOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import type { MenuProps, UploadProps } from "antd";
import clsx from "clsx";
import {
  useCallback,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import BDropdown from "../BDropdown/BDropdown";
import BTextArea from "../BTextArea/BTextArea";
import BUpload from "../BUpload/BUpload";
import "./BInputChat.scss";

export type BInputChatAttachType = "image" | "file";

export type BInputChatProps = {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  placeholder?: string;
  onAttach?: (files: File[], type: BInputChatAttachType) => void;
  onMicClick?: () => void;
  /** Temporarily hide until attach is ready. Default true. */
  showAttach?: boolean;
  /** Temporarily hide until voice is ready. Default true. */
  showMic?: boolean;
  /** Gemini-style attachment row rendered above the textarea. */
  attachments?: ReactNode;
  disabled?: boolean;
  className?: string;
};

const BInputChat = ({
  value,
  defaultValue = "",
  onChange,
  onSubmit,
  placeholder = "Hỏi bất cứ điều gì",
  onAttach,
  onMicClick,
  showAttach = true,
  showMic = true,
  attachments,
  disabled = false,
  className,
}: BInputChatProps) => {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const currentValue = isControlled ? value : internalValue;

  const imageTriggerRef = useRef<HTMLButtonElement>(null);
  const fileTriggerRef = useRef<HTMLButtonElement>(null);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const next = event.target.value;
      if (!isControlled) {
        setInternalValue(next);
      }
      onChange?.(next);
    },
    [isControlled, onChange],
  );

  const handleSubmit = useCallback(() => {
    if (disabled) {
      return;
    }
    onSubmit?.(currentValue.trim());
  }, [currentValue, disabled, onSubmit]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleBeforeImageUpload = useCallback<NonNullable<UploadProps["beforeUpload"]>>(
    (file, fileList) => {
      if (file.uid === fileList[fileList.length - 1]?.uid) {
        onAttach?.(fileList, "image");
      }
      return false;
    },
    [onAttach],
  );

  const handleBeforeFileUpload = useCallback<NonNullable<UploadProps["beforeUpload"]>>(
    (file, fileList) => {
      if (file.uid === fileList[fileList.length - 1]?.uid) {
        onAttach?.(fileList, "file");
      }
      return false;
    },
    [onAttach],
  );

  const handleMenuClick = useCallback<NonNullable<MenuProps["onClick"]>>(
    ({ key }) => {
      if (key === "image") {
        imageTriggerRef.current?.click();
        return;
      }
      if (key === "file") {
        fileTriggerRef.current?.click();
      }
    },
    [],
  );

  const menuItems: MenuProps["items"] = [
    {
      key: "image",
      icon: <FileImageOutlined />,
      label: "Thêm hình ảnh",
    },
    //TODO: ẩn tạm
    // {
    //   key: "file",
    //   icon: <PaperClipOutlined />,
    //   label: "Thêm tệp",
    // },
  ];

  return (
    <div
      className={clsx("b-input-chat", className, {
        "b-input-chat--disabled": disabled,
        "b-input-chat--has-attachments": Boolean(attachments),
      })}
    >
      {attachments ? <div className="b-input-chat__attachments">{attachments}</div> : null}

      <div className="b-input-chat__body">
        <BTextArea
          className="b-input-chat__textarea"
          value={currentValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoSize={{ minRows: 1, maxRows: 6 }}
          variant="borderless"
        />
      </div>

      {showAttach || showMic ? (
        <div className="b-input-chat__toolbar">
          {showAttach ? (
            <BDropdown
              trigger={["click"]}
              disabled={disabled}
              menu={{ items: menuItems, onClick: handleMenuClick }}
              overlayClassName="b-input-chat__attach-menu"
              placement="topLeft"
            >
              <button
                type="button"
                className="b-input-chat__plus"
                disabled={disabled}
                aria-label="Đính kèm"
              >
                <PlusOutlined />
              </button>
            </BDropdown>
          ) : (
            <span />
          )}

          {showMic ? (
            <button
              type="button"
              className="b-input-chat__mic"
              disabled={disabled}
              aria-label="Micro"
              onClick={onMicClick}
            >
              <AudioOutlined />
            </button>
          ) : null}
        </div>
      ) : null}

      {showAttach ? (
        <div className="b-input-chat__uploads">
          <BUpload
            accept="image/*"
            multiple
            fileList={[]}
            beforeUpload={handleBeforeImageUpload}
            showUploadList={false}
            disabled={disabled}
          >
            <button
              ref={imageTriggerRef}
              type="button"
              className="b-input-chat__upload-trigger"
              tabIndex={-1}
              aria-hidden
            />
          </BUpload>
          <BUpload
            fileList={[]}
            beforeUpload={handleBeforeFileUpload}
            showUploadList={false}
            disabled={disabled}
          >
            <button
              ref={fileTriggerRef}
              type="button"
              className="b-input-chat__upload-trigger"
              tabIndex={-1}
              aria-hidden
            />
          </BUpload>
        </div>
      ) : null}
    </div>
  );
};

export default BInputChat;
